import { z } from 'zod';

/**
 * Per-MLO comp + closing-cost configuration for the Pricing ★ criteria.
 *
 * The ★ ("best option") rule is identical across every category and lender:
 *   No buydown / LLPA buydown:   lenderCredit - closingCosts            >= comp
 *   Non-LLPA buydown (1-0/2-1/3-2-1): lenderCredit - (buydownCost + closingCosts) >= comp
 * where comp counts only when it is borrower-paid; with a lender-paid comp the price is shown net
 * of LENDER_PAID_COMP_PCT, so the credit only covers the costs (UWM too, since 2026-09-14).
 * Only the two dollar amounts (comp, closingCosts) vary by category. Absence of a
 * configured value means $0 / $0.
 */

/**
 * Lender-paid broker comp, percent of the loan amount, netted out of every lender-paid price —
 * 1% for every lender, UWM included (decided 2026-09-14). UWM's quote API returns the same price
 * for lender-paid and borrower-paid, so Mana nets it for UWM exactly as for the rate sheets.
 */
export const LENDER_PAID_COMP_PCT = 1;

export const PRICING_COMP_CATEGORIES = [
  'purchase_conventional',
  'purchase_fha',
  'purchase_va',
  'refi_conventional',
  'refi_fha',
  'refi_va',
  'dscr',
  'heloc',
] as const;
export type PricingCompCategory = (typeof PRICING_COMP_CATEGORIES)[number];

export const PricingCompCategorySchema = z.enum(PRICING_COMP_CATEGORIES);

export const PRICING_COMP_CATEGORY_LABELS: Record<PricingCompCategory, string> = {
  purchase_conventional: 'Purchase · Conventional',
  purchase_fha: 'Purchase · FHA',
  purchase_va: 'Purchase · VA',
  refi_conventional: 'Rate/Term Refi · Conventional',
  refi_fha: 'Rate/Term Refi · FHA',
  refi_va: 'Rate/Term Refi · VA',
  dscr: 'DSCR · Investor (purchase, refi & cash-out)',
  heloc: 'HELOC',
};

// UWM purposeTypeId values used by the pricing layout.
export const PURCHASE_PURPOSE_ID = '1'; // Purchase home
export const REFI_1ST_PURPOSE_ID = '3'; // Refinance 1st mortgage

export type LoanTypeFamily = 'conventional' | 'fha' | 'va';

/** Map a loan-type display value (e.g. "Conventional ARM", "FHA", "VA") to its family. */
export function loanTypeFamily(display: string | null | undefined): LoanTypeFamily | null {
  const s = (display ?? '').toLowerCase();
  // Jumbo loans are conventional (non-government) loans for the comp / closing-cost criteria.
  if (s.includes('conventional') || s.includes('jumbo')) return 'conventional';
  if (s.includes('fha')) return 'fha';
  if (/\bva\b/.test(s) || s.includes('va ') || s === 'va' || s.startsWith('va')) return 'va';
  return null;
}

/**
 * Resolve the comp/closing category for a quote. Returns null when the scenario
 * isn't one the ★ criteria covers (e.g. a cash-out refi, or a loan type other
 * than Conventional/FHA/VA) — the caller then stars nothing.
 */
export function resolvePricingCategory(args: {
  productType: string; // 'mortgage' | 'heloc'
  purposeTypeId?: string | null; // '1' | '3' | ...
  loanTypeDisplay?: string | null; // e.g. 'Conventional', 'FHA', 'VA'
}): PricingCompCategory | null {
  if (args.productType === 'heloc') return 'heloc';
  const purpose =
    args.purposeTypeId === PURCHASE_PURPOSE_ID
      ? 'purchase'
      : args.purposeTypeId === REFI_1ST_PURPOSE_ID
        ? 'refi'
        : null;
  if (!purpose) return null;
  const fam = loanTypeFamily(args.loanTypeDisplay);
  if (!fam) return null;
  return `${purpose}_${fam}` as PricingCompCategory;
}

/**
 * Same category resolution for the Other Lenders Pricing scenario inputs:
 * loanType 'Conventional' | 'FHA' | 'VA' | 'HELOC', loanPurpose 'Purchase' |
 * 'Rate/Term Refinance' | 'Refi Cash-Out' … Cash-out and other loan types → null.
 */
export function resolveLenderPricingCategory(args: {
  loanType?: string | null;
  loanPurpose?: string | null;
  lienPosition?: string | null;
}): PricingCompCategory | null {
  const lt = (args.loanType ?? '').toLowerCase();
  if (lt === 'heloc' || (args.lienPosition ?? '').toLowerCase() === 'second') return 'heloc';
  // DSCR has one category of its own covering every purpose — cash-out is the common investor
  // refinance, so unlike the agency rows it is starred against the DSCR comp / closing costs.
  if (lt === 'dscr') return 'dscr';
  const purposeRaw = (args.loanPurpose ?? '').toLowerCase();
  const purpose = purposeRaw.startsWith('purchase') ? 'purchase' : /rate\s*\/?\s*term/.test(purposeRaw) ? 'refi' : null;
  if (!purpose) return null;
  const fam = loanTypeFamily(args.loanType);
  if (!fam) return null;
  return `${purpose}_${fam}` as PricingCompCategory;
}

export interface PricingCompValues {
  comp: number; // MLO comp threshold, whole dollars
  closingCosts: number; // closing costs, whole dollars
}
export type PricingCompConfigMap = Partial<Record<PricingCompCategory, PricingCompValues>>;

export const PricingCompValuesSchema = z.object({
  comp: z.number().int().min(0).max(10_000_000),
  closingCosts: z.number().int().min(0).max(10_000_000),
});

/** Body of PUT /api/pricing/comp-config — a full or partial map of categories. */
export const PricingCompConfigInputSchema = z.object({
  categories: z.record(PricingCompCategorySchema, PricingCompValuesSchema),
});
export type PricingCompConfigInput = z.infer<typeof PricingCompConfigInputSchema>;
